import React, { useState } from 'react';

import { Box, Typography, MenuItem, MenuList, useTheme } from '@material-ui/core';
import { BiIdCard } from 'react-icons/bi';
import clsx from 'clsx';
import { TbPencil } from 'react-icons/tb';
import { RiDeleteBin6Line } from 'react-icons/ri';

import { Qualifications } from '@vyce/core/src/modules';
import { IconStatusComponent } from '@vyce/core/src/components/Statuses/IconStatusComponent';
import { TextStatusComponent } from '@vyce/core/src/components/Statuses/TextStatusComponent';

import { QualificationType } from '../../types';
import useStyles from '../../styles';
import { ConfirmDialog, GridActions } from '../../../../components';
import { useBooleanState } from '../../../../hooks';

interface Props {
  isHiring?: boolean;
  qualification: QualificationType;
  onEditClick: (id: string) => void;
  deleteCustomQualification?: (id: string) => Promise<void>;
}

export const QualificationItem: React.FC<Props> = ({
  qualification,
  isHiring = false,
  onEditClick,
  deleteCustomQualification,
}) => {
  const { issuer, verified = false, source, title } = qualification;
  const classes = useStyles();
  const theme = useTheme();
  const [open, setOpen] = useState<boolean>(false);
  const [isConfirmDialogOpen, setConfirmDialogOpen, setConfirmDialogClose] = useBooleanState(false);

  const handleOpen = () => {
    setOpen(true);
  };

  const handleClose = () => {
    setOpen(false);
  };

  const handleDeleteQualification = () => {
    deleteCustomQualification?.(qualification.uuid);
    setConfirmDialogClose();
  };

  return (
    <Box display="grid" gridTemplateColumns="minmax(90%, 1fr) 45px" width="100%">
      <Box
        onClick={handleOpen}
        alignItems="center"
        width="100%"
        display="flex"
        style={{ cursor: 'pointer' }}
      >
        <Box className={clsx(classes.iconContainer, { [classes.smallIconContainer]: isHiring })}>
          <BiIdCard size="22px" color={theme.palette.primary.main} />
        </Box>

        <Box marginLeft={2} display="flex" flexDirection="column" width="100%">
          <Typography
            variant="subtitle1"
            className={clsx(classes.bold, {
              [classes.smallBold]: isHiring,
              [classes.unverifiedText]: !(verified && source !== 'custom'),
              [classes.ellipcedText]: qualification.type === 'certificate',
            })}
          >
            {title}
          </Typography>

          <Typography
            variant="subtitle1"
            color="textSecondary"
            className={clsx(
              classes.ellipcedText,
              classes.smallBold,
              classes.smallText,
              classes.grayscaleBodyColor
            )}
          >
            {issuer}
          </Typography>

          <Box display="flex" alignItems="center" gridGap={8}>
            <Typography
              variant="subtitle1"
              color="textSecondary"
              className={clsx(classes.smallBold, classes.smallText)}
            >
              Status:
            </Typography>

            <TextStatusComponent
              verified={verified}
              expiry_date={(qualification.type === 'certificate' && qualification.expiry_date) || null}
            />
          </Box>
        </Box>

        <Box className={classes.verifiedLabelWrapper}>
          {qualification.type === 'certificate' && (
            <IconStatusComponent
              verified={verified}
              expiry_date={qualification.expiry_date}
              withVerifiedLabel={false}
              withUnverifiedLabel={false}
              withExpiringLabel={false}
            />
          )}
        </Box>
      </Box>

      {qualification.type === 'certificate' ? (
        <Box display="flex" width="100%" alignSelf="baseline" justifyContent="end">
          <GridActions
            variant="paper"
            renderEnrichedList={({ closeAnchorEl }) => (
              <MenuList>
                <MenuItem
                  onClick={() => {
                    onEditClick(qualification.uuid);
                    closeAnchorEl();
                  }}
                >
                  <Box display="flex" alignItems="center" gridGap={8}>
                    <TbPencil size={14} /> Edit
                  </Box>
                </MenuItem>
                <MenuItem
                  onClick={() => {
                    setConfirmDialogOpen();
                    closeAnchorEl();
                  }}
                >
                  <Box display="flex" alignItems="center" gridGap={8}>
                    <RiDeleteBin6Line size={14} /> Delete
                  </Box>
                </MenuItem>
              </MenuList>
            )}
          ></GridActions>
        </Box>
      ) : null}

      <Qualifications.QualificationPreviewDialog
        handleClose={handleClose}
        open={open}
        qualification={qualification}
      />

      <ConfirmDialog
        handleClose={setConfirmDialogClose}
        open={isConfirmDialogOpen}
        confirmText="Delete"
        cancelText="Cancel"
        title="Are you sure you want to delete this certificate?"
        handleConfirm={handleDeleteQualification}
      />
    </Box>
  );
};
